/**
 * Orchestration hook for timeline swimlanes in the transcript view.
 *
 * Bridges the flat Event[] from the transcript API to the timeline swimlane
 * pipeline: buildTimeline -> useTimeline -> layouts + selectedEvents.
 *
 * Always runs through collectRawEvents so that sourceSpans (used for
 * agent card rendering) are populated for the selected row.
 */

import { useCallback, useMemo, useState } from "react";

import type {
  Event,
  Timeline as ServerTimeline,
} from "@tsmono/inspect-common/types";

import { spliceToTimeline, type Timeline, type TimelineSpan } from "../core";
import {
  defaultMarkerConfig,
  resolveForkTimestamp,
  type MarkerConfig,
} from "../markers";
import { correctRetryTimestamps } from "../retryOrdering";
import {
  computeRowLayouts,
  rowHasEvents,
  type RowLayout,
} from "../swimlaneLayout";
import { getAgents, isSingleSpan, type SwimlaneRow } from "../swimlaneRows";
import {
  collectPathWithNavigators,
  collectRawEvents,
  computeMinimapSelection,
  getBranchPrefix,
  getParentKeyFromBranch,
  getSelectedSpans,
  parseSelection,
  type MinimapSelection,
} from "../timelineEventNodes";
import { computeTimeMapping, type TimeMapping } from "../timeMapping";

import {
  useActiveTimeline,
  type UseActiveTimelineProps,
} from "./useActiveTimeline";
import {
  useTimeline,
  type SelectOptions,
  type TimelineOptions,
  type TimelineState,
  type UseTimelineProps,
} from "./useTimeline";
import { useTimelinesArray } from "./useTimelinesArray";

// Phase lanes synthesized from the eval lifecycle (init/scoring) rather than
// agent activity. A timeline whose only child lanes are phases has no agent
// sub-structure to drill into, so it shouldn't auto-expand.
const PHASE_SPAN_TYPES = new Set(["init", "scorers"]);

const emptySourceSpans: ReadonlyMap<string, TimelineSpan> = new Map();
const emptyHighlightedKeys: ReadonlyMap<string, number> = new Map();

export interface TimelineSwimlanesData {
  /** Computed row layouts for the swimlane UI. */
  layouts: RowLayout[];
  /** Map from row key -> number of compaction regions (only for rows with compactions). */
  regionCounts: ReadonlyMap<string, number>;
  /** Row key -> highlight clip percentage (0-100) within the bar area. */
  highlightedKeys: ReadonlyMap<string, number>;
  /** Time mapping for the current node (may compress gaps). */
  timeMapping: TimeMapping;
}

export interface TimelineMinimapData {
  /** Time mapping for the root node. */
  mapping: TimeMapping;
  /** Minimap selection for the breadcrumb row. */
  selection: MinimapSelection | undefined;
}

export interface MultiTimelineNav {
  /** All available timelines (may be 1 or more). */
  timelines: Timeline[];
  /** Index of the currently active timeline. */
  activeIndex: number;
  /** Switch the active timeline by index, optionally preserving a deep link. */
  setActive: (index: number, options?: SelectOptions) => void;
}

export interface TimelineViewStack {
  /** Punched-down view stack (each entry is a spliced standalone timeline). */
  stack: ReadonlyArray<{ label: string; timeline: Timeline }>;
  /** Drill into `branch` — splice it against the base timeline's root and push onto the view stack. */
  push: (branch: TimelineSpan, label: string) => void;
  /** Drill into the branch on the swimlane row with `rowKey`. No-op when the
   *  row doesn't hold a single agent span. */
  pushByRowKey: (rowKey: string, label: string) => void;
  /** Pop the top of the view stack (back to the previous view). */
  pop: () => void;
}

export interface TimelineSelectionData {
  /** Events scoped to the selected swimlane row (or all events if no child selected). */
  events: Event[];
  /** Agent spans keyed by span ID, for attaching to EventNodes. */
  sourceSpans: ReadonlyMap<string, TimelineSpan>;
  /** Event ID to scroll to when a branch is selected (the branch separator). Null for non-branch selections. */
  branchScrollTarget: string | null;
  /** Name of the currently selected swimlane row, or the root name when nothing is selected. */
  rowName: string;
}

export interface TranscriptTimelineResult {
  /** The built Timeline. Always present (even for root-only timelines). */
  timeline: Timeline;
  /** Full useTimeline state (node, rows, navigation). */
  state: TimelineState;
  /** Whether the timeline has meaningful structure (non-empty root with children). */
  hasTimeline: boolean;
  /** Whether the timeline has agent sub-structure worth expanding (a child lane
   *  beyond the lifecycle phases init/scoring). */
  hasAgentTimeline: boolean;
  /** Swimlane rendering data. */
  swimlanes: TimelineSwimlanesData;
  /** Minimap data for the breadcrumb row. */
  minimap: TimelineMinimapData;
  /** Multi-timeline navigation. */
  multiTimeline: MultiTimelineNav;
  /** Punch-down view navigation. */
  views: TimelineViewStack;
  /** Data derived from the current swimlane selection. */
  selection: TimelineSelectionData;
}

export interface UseTranscriptTimelineOptions {
  /** The flat event array to process. */
  events: Event[];
  /** Marker configuration for swimlane layout. Defaults to `defaultMarkerConfig`. */
  markerConfig?: MarkerConfig;
  /** Timeline agent filtering/branch options. */
  timelineOptions?: TimelineOptions;
  /** Server-provided timelines (used when available instead of building from events). */
  serverTimelines?: ServerTimeline[];
  /** Props for timeline selection state. */
  timelineProps?: UseTimelineProps;
  /** Props for active timeline state. */
  activeTimelineProps?: UseActiveTimelineProps;
}

export function useTranscriptTimeline(
  options: UseTranscriptTimelineOptions
): TranscriptTimelineResult {
  const {
    events: rawEvents,
    markerConfig = defaultMarkerConfig,
    timelineOptions,
    serverTimelines,
    timelineProps,
    activeTimelineProps,
  } = options;

  const events = useMemo(() => correctRetryTimestamps(rawEvents), [rawEvents]);

  const includeUtility = timelineOptions?.includeUtility ?? false;
  const showBranches = timelineOptions?.showBranches ?? false;
  const forkRelative = timelineOptions?.forkRelative ?? false;
  const showEmptyBranches = timelineOptions?.showEmptyBranches ?? false;
  const timelines = useTimelinesArray(events, serverTimelines, {
    showEmptyBranches,
  });

  const {
    active: activeTimeline,
    activeIndex: activeTimelineIndex,
    setActive: setActiveTimelineIndex,
  } = useActiveTimeline(timelines, activeTimelineProps);
  const onSelectTimeline = timelineProps?.onSelect;

  const setActiveTimeline = useCallback(
    (index: number, options?: SelectOptions) => {
      if (index < 0 || index >= timelines.length) return;
      if (index === activeTimelineIndex) return;
      if (options) {
        onSelectTimeline?.(null, options);
      } else {
        onSelectTimeline?.(null);
      }
      setActiveTimelineIndex(index);
    },
    [
      activeTimelineIndex,
      onSelectTimeline,
      setActiveTimelineIndex,
      timelines.length,
    ]
  );

  // timelines is always non-empty here (built from events or serverTimelines),
  // so activeTimeline is guaranteed to be defined.
  const baseTimeline = activeTimeline!;

  // Punch-down: each entry is a spliced standalone view of a branch.
  // `priorSelected` stashes the selection at push time so popView can restore it.
  const [viewStack, setViewStack] = useState<
    Array<{ label: string; timeline: Timeline; priorSelected: string | null }>
  >([]);
  const [stackBase, setStackBase] = useState(baseTimeline);
  if (stackBase !== baseTimeline) {
    setStackBase(baseTimeline);
    setViewStack([]);
  }
  const timeline = viewStack.at(-1)?.timeline ?? baseTimeline;

  const pushView = useCallback(
    (branch: TimelineSpan, label: string) => {
      setViewStack((s) => [
        ...s,
        {
          label,
          timeline: spliceToTimeline(baseTimeline.root, branch),
          priorSelected: timelineProps?.selected ?? null,
        },
      ]);
      timelineProps?.onSelect(null);
    },
    [baseTimeline.root, timelineProps]
  );
  const popView = useCallback(() => {
    const top = viewStack.at(-1);
    if (!top) return;
    setViewStack((s) => s.slice(0, -1));
    timelineProps?.onSelect(top.priorSelected);
  }, [viewStack, timelineProps]);

  const state = useTimeline(timeline, timelineOptions, timelineProps);

  // Filter out child rows whose spans contain no events.
  // The parent row (depth 0) is always kept.
  const visibleRows = useMemo(
    () => state.rows.filter((row) => row.depth === 0 || rowHasEvents(row)),
    [state.rows]
  );

  const timeMapping = useMemo(
    () => computeTimeMapping(state.node),
    [state.node]
  );

  const rootTimeMapping = useMemo(
    () => computeTimeMapping(timeline.root),
    [timeline.root]
  );

  // Compute per-branch shifted time mappings when fork-relative mode is active.
  const branchMappings = useMemo(() => {
    if (!forkRelative || !showBranches) return undefined;
    return computeBranchMappings(visibleRows, timeMapping);
  }, [forkRelative, showBranches, visibleRows, timeMapping]);

  const layouts = useMemo(
    () =>
      computeRowLayouts(
        visibleRows,
        timeMapping,
        markerConfig.depth,
        markerConfig.kinds,
        branchMappings
      ),
    [
      visibleRows,
      timeMapping,
      markerConfig.depth,
      markerConfig.kinds,
      branchMappings,
    ]
  );

  const { selectedEvents, sourceSpans, branchScrollTarget } = useMemo(() => {
    // A key that matches no row is orphaned, not "nothing selected" — the
    // utility toggle hiding the selected lane is the common way to get here.
    // Scope to the root row instead of the unscoped stream, which would put
    // the just-hidden events back on screen and inflate outline/turn counts.
    // The stale key is deliberately kept so re-enabling the toggle restores
    // the selection.
    let selection = state.selected;
    let spans = getSelectedSpans(state.rows, selection);
    if (spans.length === 0 && state.rows.length > 0) {
      selection = state.rows[0]!.key;
      spans = getSelectedSpans(state.rows, selection);
    }
    const parsed = parseSelection(selection);
    if (spans.length === 0) {
      return {
        selectedEvents: events,
        sourceSpans: emptySourceSpans,
        branchScrollTarget: null as string | null,
      };
    }

    // Branch-tree timelines: render the root→selected path with inline
    // fork navigators at each fork-point anchor.
    const rowKey = parsed?.rowKey ?? "";
    const row = state.rows.find((r) => r.key === rowKey);
    if (
      spans.length === 1 &&
      (row?.branch || (spans[0]?.branches.length ?? 0) > 0)
    ) {
      const collected = collectPathWithNavigators(state.rows, rowKey, events);
      return {
        selectedEvents: collected.events,
        sourceSpans: collected.sourceSpans,
        branchScrollTarget: null as string | null,
      };
    }

    // Non-branch: existing behavior
    const collected = collectRawEvents(spans, {
      includeUtility,
      regionIndex: parsed?.regionIndex ?? null,
      showBranches,
      branchPrefix: getBranchPrefix(state.rows, selection),
    });
    return {
      selectedEvents: collected.events,
      sourceSpans: collected.sourceSpans,
      branchScrollTarget: null as string | null,
    };
  }, [events, state.rows, state.selected, includeUtility, showBranches]);

  const minimapSelection = useMemo(
    () => computeMinimapSelection(state.rows, state.selected),
    [state.rows, state.selected]
  );

  // Compute region counts: for each single-span row, count compaction events + 1.
  const regionCounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const row of state.rows) {
      const firstSpan = row.spans[0];
      if (row.spans.length === 1 && firstSpan && isSingleSpan(firstSpan)) {
        const agent = firstSpan.agent;
        let compactionCount = 0;
        for (const item of agent.content) {
          if (item.type === "event" && item.event.event === "compaction") {
            compactionCount++;
          }
        }
        if (compactionCount > 0) {
          counts.set(row.key, compactionCount + 1);
        }
      }
    }
    return counts;
  }, [state.rows]);

  // Compute highlighted rows with clip percentages when a branch is selected.
  const highlightedKeys = useMemo(() => {
    const parsed = parseSelection(state.selected);
    const rowKey = parsed?.rowKey ?? "";
    const row = state.rows.find((r) => r.key === rowKey);
    if (!row?.branch) return emptyHighlightedKeys;

    const layoutByKey = new Map<string, RowLayout>();
    for (const layout of layouts) {
      layoutByKey.set(layout.key, layout);
    }

    const keys = new Map<string, number>();
    keys.set(rowKey, 100);

    let childKey = rowKey;
    while (true) {
      const parentKey = getParentKeyFromBranch(childKey);
      if (!parentKey) break;

      const branchMatch = /\/branch-([^/]*)-\d+$/.exec(childKey);
      const branchedFrom = branchMatch?.[1] ?? "";

      const parentLayout = layoutByKey.get(parentKey);
      const forkMarker = parentLayout?.markers.find(
        (m) => m.kind === "branch" && m.reference === branchedFrom
      );

      keys.set(parentKey, forkMarker?.left ?? 100);
      childKey = parentKey;
    }
    return keys;
  }, [state.selected, state.rows, layouts]);

  const hasTimeline =
    timeline.root.content.length > 0 &&
    (timeline.root.content.some(
      (item) => item.type === "span" || item.event.event === "span_begin"
    ) ||
      timeline.root.branches.length > 0);

  // True when the timeline has agent sub-structure worth expanding — i.e. a
  // child lane that isn't just a lifecycle phase (init/scoring). A bare
  // main + scoring transcript is effectively flat and defaults to collapsed.
  const hasAgentTimeline = visibleRows.some((row) => {
    if (row.depth < 1) return false;
    const rowSpan = row.spans[0];
    if (!rowSpan) return false;
    const span = getAgents(rowSpan)[0];
    return !!span && !PHASE_SPAN_TYPES.has(span.spanType ?? "");
  });

  // Compute the agent name for the outline header.
  // When a swimlane row is selected, show its name; otherwise show the root.
  const selectedRowName = useMemo(() => {
    if (!state.selected) return timeline.root.name;
    const parsed = parseSelection(state.selected);
    const rowKey = parsed?.rowKey ?? state.selected;
    const row = state.rows.find((r) => r.key === rowKey);
    return row?.name ?? timeline.root.name;
  }, [state.selected, state.rows, timeline.root.name]);

  // Group into cohesive sub-objects with memoized identities so consumers
  // can depend on a group without spurious recomputes.
  const swimlanes = useMemo(
    () => ({ layouts, regionCounts, highlightedKeys, timeMapping }),
    [layouts, regionCounts, highlightedKeys, timeMapping]
  );

  const minimap = useMemo(
    () => ({ mapping: rootTimeMapping, selection: minimapSelection }),
    [rootTimeMapping, minimapSelection]
  );

  const multiTimeline = useMemo(
    () => ({
      timelines,
      activeIndex: activeTimelineIndex,
      setActive: setActiveTimeline,
    }),
    [timelines, activeTimelineIndex, setActiveTimeline]
  );

  const pushViewByRowKey = useCallback(
    (rowKey: string, label: string) => {
      const row = state.rows.find((r) => r.key === rowKey);
      const span = row?.spans[0];
      if (span && "agent" in span) pushView(span.agent, label);
    },
    [state.rows, pushView]
  );

  const views = useMemo(
    () => ({
      stack: viewStack,
      push: pushView,
      pushByRowKey: pushViewByRowKey,
      pop: popView,
    }),
    [viewStack, pushView, pushViewByRowKey, popView]
  );

  const selection = useMemo(
    () => ({
      events: selectedEvents,
      sourceSpans,
      branchScrollTarget,
      rowName: selectedRowName,
    }),
    [selectedEvents, sourceSpans, branchScrollTarget, selectedRowName]
  );

  return {
    timeline,
    state,
    hasTimeline,
    hasAgentTimeline,
    swimlanes,
    minimap,
    multiTimeline,
    views,
    selection,
  };
}

// =============================================================================
// Fork-relative branch mapping computation
// =============================================================================

const kBranchKeyPattern = /\/branch-([^/]*)-(\d+)$/;

/**
 * Compute per-row time mappings for fork-relative layout.
 *
 * Each row is placed at its parent's fork position and extends by its own
 * duration; the domain is the max logical extent across all rows. This fills
 * the full width regardless of how root content and branch execution times
 * relate in wall-clock terms.
 */
function computeBranchMappings(
  rows: ReadonlyArray<SwimlaneRow>,
  trunkMapping: TimeMapping
): ReadonlyMap<string, TimeMapping> | undefined {
  if (!rows.some((r) => r.branch)) return undefined;

  const rowByKey = new Map<string, SwimlaneRow>();
  for (const row of rows) rowByKey.set(row.key, row);

  const root = rows.find((r) => r.depth === 0);
  const rootStart = root?.startTime.getTime() ?? 0;

  const singleSpan = (row: SwimlaneRow | undefined) => {
    const s = row?.spans[0];
    return row?.spans.length === 1 && s && isSingleSpan(s) ? s.agent : null;
  };

  // Nearest ancestor row whose logical start has been computed.
  const nearestAncestor = (key: string): SwimlaneRow | undefined => {
    let k = key;
    while (true) {
      const i = k.lastIndexOf("/");
      if (i < 0) return root;
      k = k.slice(0, i);
      if (logicalStart.has(k)) return rowByKey.get(k);
    }
  };

  // Logical start of each row (ms from a virtual origin = root content start).
  // Branch rows shift to their parent's fork position; non-branch rows inherit
  // their nearest ancestor's logical start plus their wall-clock offset from it.
  const logicalStart = new Map<string, number>();
  for (const row of rows) {
    if (!row.branch) {
      const anc = nearestAncestor(row.key);
      const ancLogical = anc ? (logicalStart.get(anc.key) ?? 0) : 0;
      const ancStart = anc?.startTime.getTime() ?? rootStart;
      logicalStart.set(
        row.key,
        ancLogical + (row.startTime.getTime() - ancStart)
      );
      continue;
    }
    const parentKey = row.key.replace(kBranchKeyPattern, "");
    const parentSpan = singleSpan(rowByKey.get(parentKey));
    const branchSpan = singleSpan(row);
    if (!parentSpan || !branchSpan) continue;
    const forkTs = resolveForkTimestamp(parentSpan, branchSpan).getTime();
    const parentStart = parentSpan.startTime(false).getTime();
    const parentLogical = logicalStart.get(parentKey) ?? 0;
    logicalStart.set(row.key, parentLogical + (forkTs - parentStart));
  }

  let maxEnd = 1;
  for (const row of rows) {
    const ls = logicalStart.get(row.key);
    if (ls === undefined) continue;
    maxEnd = Math.max(
      maxEnd,
      ls + (row.endTime.getTime() - row.startTime.getTime())
    );
  }

  const mappings = new Map<string, TimeMapping>();
  for (const row of rows) {
    const ls = logicalStart.get(row.key);
    if (ls === undefined) {
      mappings.set(row.key, trunkMapping);
      continue;
    }
    const wallStart = row.startTime.getTime();
    mappings.set(row.key, {
      toPercent: (ts) =>
        Math.max(
          0,
          Math.min(100, ((ls + (ts.getTime() - wallStart)) / maxEnd) * 100)
        ),
      hasCompression: false,
      gaps: [],
    });
  }
  return mappings;
}
